import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Plus, Edit, Trash2, Search, X, AlertCircle, Upload, Image as ImageIcon, Video, Star, FileUp, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Media state
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [existingMedia, setExistingMedia] = useState([]);
  const [mediaToDelete, setMediaToDelete] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    discount_price: '',
    cost_price: '',
    category: '',
    type: '',
    stock: '',
    featured: false,
    brand: '',
    tags: ''
  });

  // Floating horizontal scrollbar for the wide products table
  const tableScrollRef = useRef(null);
  const floatingScrollRef = useRef(null);
  const syncingScroll = useRef(false);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  // CSV import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [imageJob, setImageJob] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchProductTypes();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories?is_active=true`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        
        if (data.length > 0 && !formData.category) {
          setFormData(prev => ({ ...prev, category: data[0].code }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/product-types?is_active=true`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProductTypes(data);

        if (data.length > 0 && !formData.type) {
          setFormData(prev => ({ ...prev, type: data[0].code }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch product types:', error);
    }
  };

  const fetchProductMedia = async (productId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/products/${productId}/media`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setExistingMedia(data.media || []);
        
        // Set primary image index
        const primaryIndex = data.media.findIndex(m => m.is_primary && m.file_type === 'image');
        if (primaryIndex !== -1) {
          setPrimaryImageIndex(primaryIndex);
        }
      }
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e, fileType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      if (fileType === 'image') {
        setImageFiles(prev => [...prev, ...files]);
      } else {
        setVideoFiles(prev => [...prev, ...files]);
      }
    }
  };

  const handleFileSelect = (e, fileType) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      if (fileType === 'image') {
        setImageFiles(prev => [...prev, ...files]);
      } else {
        setVideoFiles(prev => [...prev, ...files]);
      }
    }
  };

  const removeFile = (index, fileType) => {
    if (fileType === 'image') {
      setImageFiles(prev => prev.filter((_, i) => i !== index));
      // Adjust primary index if needed
      if (primaryImageIndex === index) {
        setPrimaryImageIndex(0);
      } else if (primaryImageIndex > index) {
        setPrimaryImageIndex(prev => prev - 1);
      }
    } else {
      setVideoFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const toggleDeleteMedia = (mediaId) => {
    setMediaToDelete(prev => 
      prev.includes(mediaId) 
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUploading(true);
    
    try {
      const formDataToSend = new FormData();
      // Add product fields
      
      formDataToSend.append('name', formData.name);
      if (formData.sku) {
        formDataToSend.append('sku', formData.sku);
      }
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('cost_price', formData.cost_price);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('stock', formData.stock);
      formDataToSend.append('featured', formData.featured);
      if (formData.discount_price) {
        formDataToSend.append('discount_price', formData.discount_price);
      }
      if (formData.brand) {
        formDataToSend.append('brand', formData.brand);
      }
      if (formData.tags) {
        formDataToSend.append('tags', formData.tags);
      }
      if (formData.show_in_hot_selling) {
        formDataToSend.append('show_in_hot_selling', formData.show_in_hot_selling);
      }
      if (formData.show_in_pre_orders) {
        formDataToSend.append('show_in_pre_orders', formData.show_in_pre_orders);
      }
      
      // Add media files
      imageFiles.forEach(file => {
        formDataToSend.append('images', file);
      });
      
      videoFiles.forEach(file => {
        formDataToSend.append('videos', file);
      });
      
      // Calculate primary index considering existing media
      if (editingProduct) {
        const remainingExistingImages = existingMedia.filter(
          m => m.file_type === 'image' && !mediaToDelete.includes(m.id)
        );
        const totalPrimaryIndex = primaryImageIndex;
        formDataToSend.append('primary_image_index', totalPrimaryIndex);
        
        // Add media to delete
        if (mediaToDelete.length > 0) {
          formDataToSend.append('delete_media_ids', mediaToDelete.join(','));
        }
      } else {
        formDataToSend.append('primary_image_index', primaryImageIndex);
      }
      
      const url = editingProduct 
        ? `${BACKEND_URL}/api/admin/products/${editingProduct.id}`
        : `${BACKEND_URL}/api/admin/products`;

console.log(formDataToSend)

      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        credentials: 'include',
        body: formDataToSend
      });
      
      if (response.ok) {
        toast.success(editingProduct ? 'Product updated successfully' : 'Product created successfully');
        setShowModal(false);
        setEditingProduct(null);
        resetForm();
        fetchProducts();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save product');
        toast.error(errorData.detail || 'Failed to save product');
      }
    } catch (error) {
      console.error('Failed to save product:', error);
      setError('Failed to save product. Please try again.');
      toast.error('Failed to save product');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/products/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Product deleted successfully');
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleEdit = async (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku: product.sku || '',
      description: product.description,
      price: product.price,
      discount_price: product.discount_price || '',
      cost_price: product.cost_price || '',
      category: product.category,
      type: product.type,
      stock: product.stock,
      featured: product.featured,
      brand: product.brand || '',
      tags: product.tags ? product.tags.join(', ') : ''
    });
    
    // Fetch existing media
    await fetchProductMedia(product.id);
    
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      description: '',
      price: '',
      discount_price: '',
      cost_price: '',
      category: categories.length > 0 ? categories[0].code : '',
      type: productTypes.length > 0 ? productTypes[0].code : '',
      stock: '',
      featured: false,
      brand: '',
      tags: ''
    });
    setImageFiles([]);
    setVideoFiles([]);
    setPrimaryImageIndex(0);
    setExistingMedia([]);
    setMediaToDelete([]);
    setError('');
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/products/import/template`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to download template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template');
    }
  };

  const pollImageJob = async (jobId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/products/import/jobs/${jobId}`, {
        credentials: 'include'
      });
      if (!response.ok) return;

      const job = await response.json();
      setImageJob(job);

      if (job.status === 'processing') {
        setTimeout(() => pollImageJob(jobId), 2000);
      } else {
        toast.success(`Image import finished: ${job.succeeded} succeeded, ${job.failed} failed`);
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to poll image import job:', error);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    setImporting(true);
    setImportResult(null);
    setImageJob(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', importFile);

      const response = await fetch(`${BACKEND_URL}/api/admin/products/import`, {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        setImportResult(data);
        toast.success(`Import complete: ${data.created} created, ${data.updated} updated, ${data.failed} failed`);
        fetchProducts();
        if (data.image_job_id) {
          pollImageJob(data.image_job_id);
        }
      } else {
        toast.error(data.detail || 'Failed to import CSV');
      }
    } catch (error) {
      console.error('Failed to import products:', error);
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (code) => {
    const category = categories.find(cat => cat.code === code);
    return category ? category.name : code;
  };

  const getTypeName = (code) => {
    const type = productTypes.find(pt => pt.code === code);
    return type ? type.name : code;
  };

  // Keep the floating scrollbar's width/visibility and scroll position in
  // sync with the actual (wide) table.
  useEffect(() => {
    const tableEl = tableScrollRef.current;
    if (!tableEl) return;

    const update = () => {
      setTableScrollWidth(tableEl.scrollWidth);
      setNeedsHorizontalScroll(tableEl.scrollWidth > tableEl.clientWidth + 1);
    };

    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(tableEl);
    window.addEventListener('resize', update);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [filteredProducts, loading]);

  const handleTableScroll = () => {
    if (syncingScroll.current || !floatingScrollRef.current || !tableScrollRef.current) return;
    syncingScroll.current = true;
    floatingScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    syncingScroll.current = false;
  };

  const handleFloatingScroll = () => {
    if (syncingScroll.current || !floatingScrollRef.current || !tableScrollRef.current) return;
    syncingScroll.current = true;
    tableScrollRef.current.scrollLeft = floatingScrollRef.current.scrollLeft;
    syncingScroll.current = false;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Products</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage your product inventory</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setImportFile(null);
                setImportResult(null);
                setImageJob(null);
                setShowImportModal(true);
              }}
              className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <FileUp size={20} />
              Import CSV
            </button>
            <button
              onClick={() => {
                setEditingProduct(null);
                resetForm();
                setShowModal(true);
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700"
            >
              <Plus size={20} />
              Add Product
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.code}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto scrollbar-hide" ref={tableScrollRef} onScroll={handleTableScroll}>
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Image</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">SKU</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Product</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Description</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Category</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Type</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Brand</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Price</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Cost Price</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Stock</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Tags</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Rating</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Featured</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Hot Selling</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Pre-Order</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Is Deleted</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Created</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Updated</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="19" className="text-center py-8 text-gray-500">Loading...</td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="19" className="text-center py-8 text-gray-500">No products found</td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => (
                    <tr key={product.id} className="border-t dark:border-gray-700">
                      <td className="py-3 px-4">
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {product.sku || <span className="text-gray-400 italic">none</span>}
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {product.name}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={product.description}>
                        {product.description}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs">
                          {getCategoryName(product.category)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {getTypeName(product.type)}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {product.brand || '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white font-medium whitespace-nowrap">
                        ₹{product.price}
                        {product.discount_price && (
                          <span className="text-sm text-green-600 ml-2">(₹{product.discount_price})</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        ₹{product.cost_price}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{product.stock}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 max-w-[160px] truncate" title={(product.tags || []).join(', ')}>
                        {product.tags && product.tags.length > 0 ? product.tags.join(', ') : '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {(product.rating || 0).toFixed(1)} ★ ({product.review_count || 0})
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          product.featured
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {product.featured ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          product.show_in_hot_selling
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {product.show_in_hot_selling ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          product.show_in_pre_orders
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {product.show_in_pre_orders ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          product.is_deleted
                            ? 'bg-red-100 text-yellow-800 dark:bg-yellow-900 dark:text-black-200'
                            : 'bg-green-100 text-gray-800 dark:bg-gray-700 dark:text-black-300'
                        }`}>
                          {product.is_deleted ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {product.created_at ? new Date(product.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Floating horizontal scrollbar - mirrors the table's scroll so it stays
            reachable at the bottom of the viewport even when scrolled far down the page */}
        {needsHorizontalScroll && (
          <div
            className="sticky bottom-0 z-30 overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow"
            style={{ height: '14px' }}
            ref={floatingScrollRef}
            onScroll={handleFloatingScroll}
          >
            <div style={{ height: '1px', width: `${tableScrollWidth}px` }} />
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingProduct ? 'Edit Product' : 'Add Product'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={24} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                  <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
                  <span className="text-red-600 dark:text-red-400 text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Product Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Product Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                      <textarea
                        required
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        rows="3"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SKU</label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({...formData, sku: e.target.value})}
                        placeholder="Auto-generated if left blank"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category *</label>
                      <select
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.code}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                      <select
                        required
                        value={formData.type}
                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {productTypes.length === 0 && (
                          <option value="">No product types yet</option>
                        )}
                        {productTypes.map((pt) => (
                          <option key={pt.id} value={pt.code}>
                            {pt.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Manage available types from the Product Types admin page.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Selling Price (₹) *</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cost Price (₹) *</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={formData.cost_price}
                        onChange={(e) => setFormData({...formData, cost_price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discount Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.discount_price}
                        onChange={(e) => setFormData({...formData, discount_price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock *</label>
                      <input
                        type="number"
                        required
                        value={formData.stock}
                        onChange={(e) => setFormData({...formData, stock: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({...formData, brand: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma-separated)</label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => setFormData({...formData, tags: e.target.value})}
                        placeholder="anime, collectible, limited"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.featured}
                          onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Featured Product</span>
                      </label>
                    </div>

                    <div className="col-span-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.show_in_hot_selling}
                          onChange={(e) => setFormData({
                            ...formData,
                            show_in_hot_selling: e.target.checked
                          })}
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show in Hot Selling Section</span>
                      </label>
                    </div>

                    <div className="col-span-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.show_in_pre_orders}
                          onChange={(e) => setFormData({
                            ...formData,
                            show_in_pre_orders: e.target.checked
                          })}
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show in Pre-Orders Section</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Existing Media (when editing) */}
                {editingProduct && existingMedia.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Existing Media</h3>
                    <div className="grid grid-cols-4 gap-4">
                      {existingMedia.map((media, index) => (
                        <div key={media.id} className="relative group">
                          <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                            {media.file_type === 'image' ? (
                              <img src={media.file_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <video src={media.file_url} className="w-full h-full object-cover" />
                            )}
                          </div>
                          
                          {/* Controls overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {media.file_type === 'image' && !mediaToDelete.includes(media.id) && (
                              <button
                                type="button"
                                onClick={() => {
                                  const imageIndex = existingMedia.filter(m => m.file_type === 'image').indexOf(media);
                                  setPrimaryImageIndex(imageIndex);
                                }}
                                className={`p-2 rounded ${
                                  media.is_primary ? 'bg-yellow-500' : 'bg-white/20 hover:bg-white/40'
                                }`}
                                title="Set as primary"
                              >
                                <Star size={16} className="text-white" />
                              </button>
                            )}
                            
                            <button
                              type="button"
                              onClick={() => toggleDeleteMedia(media.id)}
                              className={`p-2 rounded ${
                                mediaToDelete.includes(media.id) ? 'bg-red-600' : 'bg-white/20 hover:bg-white/40'
                              }`}
                              title={mediaToDelete.includes(media.id) ? 'Cancel delete' : 'Delete'}
                            >
                              <Trash2 size={16} className="text-white" />
                            </button>
                          </div>
                          
                          {/* Media type badge */}
                          <div className="absolute top-2 left-2">
                            {media.file_type === 'image' ? (
                              <ImageIcon size={16} className="text-white drop-shadow" />
                            ) : (
                              <Video size={16} className="text-white drop-shadow" />
                            )}
                          </div>
                          
                          {/* Delete overlay */}
                          {mediaToDelete.includes(media.id) && (
                            <div className="absolute inset-0 bg-red-600 bg-opacity-75 flex items-center justify-center">
                              <span className="text-white font-semibold">Will Delete</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    {editingProduct ? 'Add More Images' : 'Product Images *'}
                  </h3>
                  
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={(e) => handleDrop(e, 'image')}
                  >
                    <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg mb-2 text-gray-900 dark:text-white">Drag & drop images here</p>
                    <p className="text-sm text-gray-500 mb-4">or</p>
                    
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, 'image')}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        Browse Images
                      </span>
                    </label>
                    
                    <p className="text-xs text-gray-500 mt-4">
                      Supported: JPEG, PNG, WebP, GIF (Max 5MB each)
                    </p>
                  </div>

                  {/* Image Previews */}
                  {imageFiles.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Selected Images ({imageFiles.length})
                      </p>
                      <div className="grid grid-cols-4 gap-4">
                        {imageFiles.map((file, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Controls */}
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const totalExistingImages = existingMedia.filter(
                                    m => m.file_type === 'image' && !mediaToDelete.includes(m.id)
                                  ).length;
                                  setPrimaryImageIndex(totalExistingImages + index);
                                }}
                                className={`p-2 rounded ${
                                  (!editingProduct && primaryImageIndex === index) ||
                                  (editingProduct && primaryImageIndex === existingMedia.filter(m => m.file_type === 'image' && !mediaToDelete.includes(m.id)).length + index)
                                    ? 'bg-yellow-500'
                                    : 'bg-white/20 hover:bg-white/40'
                                }`}
                                title="Set as primary"
                              >
                                <Star size={16} className="text-white" />
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => removeFile(index, 'image')}
                                className="p-2 bg-white/20 hover:bg-white/40 rounded"
                              >
                                <Trash2 size={16} className="text-white" />
                              </button>
                            </div>
                            
                            {/* File info */}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                              {file.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Video Upload */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    {editingProduct ? 'Add More Videos' : 'Product Videos (Optional)'}
                  </h3>
                  
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={(e) => handleDrop(e, 'video')}
                  >
                    <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg mb-2 text-gray-900 dark:text-white">Drag & drop videos here</p>
                    <p className="text-sm text-gray-500 mb-4">or</p>
                    
                    <input
                      type="file"
                      multiple
                      accept="video/*"
                      onChange={(e) => handleFileSelect(e, 'video')}
                      className="hidden"
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer">
                      <span className="inline-block px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                        Browse Videos
                      </span>
                    </label>
                    
                    <p className="text-xs text-gray-500 mt-4">
                      Supported: MP4, MOV, WebM (Max 100MB each)
                    </p>
                  </div>

                  {/* Video Previews */}
                  {videoFiles.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Selected Videos ({videoFiles.length})
                      </p>
                      <div className="grid grid-cols-4 gap-4">
                        {videoFiles.map((file, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                              <video
                                src={URL.createObjectURL(file)}
                                className="w-full h-full object-cover"
                                controls
                              />
                            </div>
                            
                            <button
                              type="button"
                              onClick={() => removeFile(index, 'video')}
                              className="absolute top-2 right-2 p-2 bg-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={16} className="text-white" />
                            </button>
                            
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                              {file.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Validation message */}
                {!editingProduct && imageFiles.length === 0 && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    * At least one product image is required
                  </p>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProduct(null);
                      resetForm();
                    }}
                    disabled={uploading}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading || (!editingProduct && imageFiles.length === 0)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Uploading...
                      </>
                    ) : (
                      editingProduct ? 'Update Product' : 'Create Product'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Import Products from CSV</h2>
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={24} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Rows are matched by SKU — existing SKUs get updated, new SKUs create new products.
                Category and type columns must use existing category/type codes. To attach images,
                add public image URLs in the image_urls column (separate multiple with a semicolon;
                the first is used as the primary image) — they're fetched and processed in the
                background after the import completes.
              </p>

              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="mb-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <Download size={16} />
                Download CSV template
              </button>

              <form onSubmit={handleImportSubmit} className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-6 text-center border-gray-300 dark:border-gray-600">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <FileUp className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                    <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Choose CSV File
                    </span>
                  </label>
                  {importFile && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">{importFile.name}</p>
                  )}
                </div>

                {importResult && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600 dark:text-green-400">Created: {importResult.created}</span>
                      <span className="text-blue-600 dark:text-blue-400">Updated: {importResult.updated}</span>
                      <span className="text-red-600 dark:text-red-400">Failed rows: {importResult.failed}</span>
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div className="max-h-48 overflow-y-auto text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {importResult.errors.map((err, i) => (
                          <div key={i}>
                            Row {err.row} ({err.field}): {err.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {imageJob && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      {imageJob.status === 'processing' && (
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-600 border-t-transparent"></div>
                      )}
                      Image processing: {imageJob.processed}/{imageJob.total}
                      {imageJob.status !== 'processing' && ' (done)'}
                    </p>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600 dark:text-green-400">Succeeded: {imageJob.succeeded}</span>
                      <span className="text-red-600 dark:text-red-400">Failed: {imageJob.failed}</span>
                    </div>
                    {imageJob.errors?.length > 0 && (
                      <div className="max-h-48 overflow-y-auto text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {imageJob.errors.map((err, i) => (
                          <div key={i}>
                            {err.sku}: {err.message} {err.url ? `(${err.url})` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    disabled={importing}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={importing || !importFile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {importing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Importing...
                      </>
                    ) : (
                      'Import'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Products;